import { Reveal, StaggerGroup, StaggerItem } from "./Reveal";
import { Layers, Brain, Share2, BarChart3 } from "lucide-react";
import { useI18n } from "../i18n/LanguageContext";

const ARCH_IMG =
  "https://images.unsplash.com/photo-1506399309177-3b43e99fead2";

const PILLAR_ICONS = [Layers, Brain, Share2, BarChart3];

export const Technology = () => {
  const { t } = useI18n();
  const pillars = t("tech.pillars");
  return (
    <section
      id="technology"
      className="relative py-28 md:py-36 px-6 bg-[#05101f] overflow-hidden"
    >
      <div className="max-w-6xl mx-auto">
        <Reveal>
          <p className="font-mono text-[13px] tracking-[0.3em] uppercase text-[#4BE3FF] mb-5">
            {t("tech.kicker")}
          </p>
          <h2 className="font-display font-bold tracking-tight text-4xl md:text-5xl lg:text-6xl max-w-3xl leading-[1.02]">
            {t("tech.titleA")}
            <span className="text-[#A7B4C2]">{t("tech.titleB")}</span>
          </h2>
        </Reveal>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 mt-16 items-stretch">
          {/* Architecture visual */}
          <Reveal className="lg:col-span-5" delay={0.1}>
            <div className="relative h-full min-h-[340px] rounded-2xl overflow-hidden border border-[#A7B4C2]/15">
              <img
                src={ARCH_IMG}
                alt="Rexxengine architecture"
                className="absolute inset-0 w-full h-full object-cover opacity-40 [mask-image:radial-gradient(80%_80%_at_40%_30%,#000,transparent)]"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#05101f] via-[#05101f]/40 to-transparent" />
              <div className="absolute inset-0 grid-lines opacity-30" />
              <div className="relative z-10 p-8 h-full flex flex-col justify-end">
                <p className="font-mono text-[12px] tracking-widest uppercase text-[#4BE3FF]">
                  {t("tech.core")}
                </p>
                <p className="font-display text-2xl mt-2 text-white">
                  {t("tech.flow")}
                </p>
                <div className="mt-6 flex flex-wrap gap-2 font-mono text-[11px] text-[#A7B4C2]">
                  {["BM25", "Dense Vectors", "Graph DB", "Reranker", "LLM Layer"].map(
                    (tag) => (
                      <span
                        key={tag}
                        className="border border-white/10 rounded-full px-2.5 py-1"
                      >
                        {tag}
                      </span>
                    )
                  )}
                </div>
              </div>
            </div>
          </Reveal>

          {/* Pillars grid */}
          <StaggerGroup className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-5">
            {pillars.map((p, i) => {
              const Icon = PILLAR_ICONS[i];
              return (
                <StaggerItem key={p.title}>
                  <div
                    data-testid={`tech-pillar-${i}`}
                    className="group h-full rounded-2xl border border-[#A7B4C2]/15 bg-[#0A1A2F] p-7 hover:border-[#4BE3FF]/40 transition-colors duration-500"
                  >
                    <div className="flex items-center justify-center w-12 h-12 rounded-xl border border-[#1E6FFF]/40 text-[#4BE3FF] group-hover:cyan-glow transition-all duration-500">
                      <Icon className="w-5 h-5" />
                    </div>
                    <h3 className="font-display font-semibold text-xl mt-6 text-white">
                      {p.title}
                    </h3>
                    <p className="font-ui text-[14px] text-[#A7B4C2] mt-3 leading-relaxed">
                      {p.desc}
                    </p>
                  </div>
                </StaggerItem>
              );
            })}
          </StaggerGroup>
        </div>
      </div>
    </section>
  );
};
